import { post, get, put, del } from 'aws-amplify/api';
import { Project, ProjectPayload, ProjectDocumentRef } from 'types/project';
import type { EntityAssociation, LegalEntity, LegalEntityPayload } from 'types/legal-entity';
import { latestCompleteRunLinks } from 'types/legal-entity';
import type { RunEntityLinks } from 'types/legal-entity';
import { AppUser } from 'types/app-user';
//import { ProjectType } from 'types/project-type';
//import { ModelType } from 'types/model-type';
import { StandardPromptOptions, ConfigType, ArtefactPromptOptions, PromptDefinition, PromptSet, ConfigByType } from 'types/prompt-config';

import { DataType } from 'types';

import { fetchAuthSession } from 'aws-amplify/auth';
import { update } from 'lodash-es';

import useAuth from 'hooks/useAuth';
import { HubDocument, DocumentPipelineStatus, DocumentPipelineRun, DocumentPipelineState } from 'types/document';
import type { ServiceStatus } from 'types/service-status';
import { PromptDialogContext, PromptResponseItem, PromptResultResponseItem, ListPromptResultsOutput, RunPhaseUsage } from 'components/versionPrompts/types';



async function getToken() {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error('No id token available');
  }

  return idToken;
}

type GetConfigsResponse = {
  status: boolean;
  data?: ConfigByType;
  error?: string | string[];
};

type UpdateConfigResponse<T extends ConfigType = ConfigType> = {
  status: boolean;
  data?: {
    type: T;
    config: ConfigByType[T];
  };
  error?: string | string[];
};


export async function listVersionPrompts(payload: any): Promise<PromptResponseItem[]> {
  const restOp = post({
    apiName: 'ntab',
    path: '/version-prompts',
    options: {
      body: payload,
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      }
    }
  });

  const response = await restOp.response;
  const body = await response.body.json() as ListVersionPromptsResponse;
  //console.log('[SANITY] listDocs response body:', body);

  if (!body.status) throw new Error(body.error || 'Failed to list version prompts');

  const data = (body.data ?? []) as PromptResponseItem[];
  return data;
}


export async function listPromptResults(payload: any): Promise<ListPromptResultsOutput> {
  const restOp = post({
    apiName: 'ntab',
    path: '/version-prompts',
    options: {
      body: payload,
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      }
    }
  });

  const response = await restOp.response;
  const body = await response.body.json() as ListVersionPromptResultsResponse;
  //console.log('[SANITY] listDocs response body:', body);

  if (!body.status) throw new Error(body.error || 'Failed to list prompt results');

  //const data = (body.data ?? []) as PromptResultResponseItem[];
  //const data2 = (body.data2 ?? []) as any;
  //const data3 = (body.data3  ?? "") as string;
  //console.log(data2);
  //console.log(data3);

  return {
    data: (body.data ?? []) as PromptResultResponseItem[],
    data2: body.data2 ?? null,
    data3: (body.data3 ?? '') as string,
    // Null for runs that predate usage recording, or a phase that made no model calls.
    usage: (body.usage ?? null) as RunPhaseUsage | null,
    // Only present when the caller asked for it; the dialog does not need it just to render.
    runContext: (body.runContext ?? null) as Record<string, unknown> | null,
    documentMetadata: (body.documentMetadata ?? null) as Record<string, unknown> | null
  };
}

export async function listMemberships(): Promise<AppUser[] | null> {
  const restOp = get({
    apiName: 'ntab',
    path: '/organisation/members',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      }
    }
  });
  const response = await restOp.response;
  const body = await response.body.json() as ListUsersResponse;
  if (!body.status) throw new Error(body.error || 'Failed to list memberships');

  const data = (body.data ?? []).map(mapUser);
  return data;
}


export async function updateOrganisation(payload: any): Promise<boolean | null> {
  const restOp = put({
    apiName: 'ntab',
    path: '/organisation',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body: payload
    }
  });
  const response = await restOp.response;
  const body = await response.body.json() as GetGenericResponse;
  if (!body.status) throw new Error(body.error || 'Failed to update organisation');
  return true;
}



type UserApi = {
  pk: string;
  sk: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  orgId: string;
  orgName: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  avatar?: string;
  roles?: string[];
  status?: number;
  created_at?: number;
  user_id?: string; // Optional field for compatibility with different API responses
};
type GetUserResponse = {
  status: boolean;
  user?: UserApi;
};
type ListUsersResponse = {
  status: boolean;
  data?: UserApi[];
  error?: string;
};

type GetGenericResponse = {
  status: boolean;
  data?: any;
  error?: string;
};


function mapUser(u: UserApi): AppUser {
  //console.log(u);
  let userId = u.user_id || null;
  if (!userId && u.pk.startsWith('USR|')) {
    userId = u.pk.slice(4);
  }
  return {
    userId: userId || '',
    firstName: u.first_name ?? '',
    lastName: u.last_name ?? '',
    email: u.email,
    phone: u.phone,
    //orgId: u.orgId,
    orgName: u.orgName,
    contactName: u.contactName ?? '',
    contactPhone: u.contactPhone ?? '',
    contactEmail: u.contactEmail ?? '',
    roles: u.roles || [],
    status: u.status || 0,
    avatar: u.avatar || undefined,
    isInvite: u.sk.slice(0, 3) === 'INV', // If sk starts with 'INV', it's an invite
    isMember: u.sk.slice(0, 3) === 'USR' || (u.pk.slice(0, 3) === 'USR' && u.sk === 'DTL'), // If sk starts with 'USR' OR pk starts with 'USR' and sk is 'DTL', it's a member
    isBlocked: u.status === 0 || u.status === 9,
    isArchived: u.status === 9,
    isSelf: false,
    addedTimeStamp: u.created_at || Date.now()
  };
}
export async function updateUser(payload: any): Promise<boolean | null> {
  const restOp = put({
    apiName: 'ntab',
    path: '/users',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body: payload
    }
  });
  const response = await restOp.response;
  const body = await response.body.json() as GetUserResponse;
  if (!body.status) throw new Error('Failed to update user');
  return true;
}

export async function getUser(user: any): Promise<AppUser | null> {
  const restOp = post({
    apiName: 'ntab',
    path: '/users',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body: user
    }

  });
  const response = await restOp.response;
  const body = await response.body.json() as GetUserResponse;
  if (!body.status || !body.user) return null;
  return mapUser(body.user);
}

type ProjectApi = {
  item_id: string;
  name: string;
  updated_at?: number;
  description?: string;
  documents?: ProjectDocumentRef[];
  item_status?: number;
  type: string;
  created_by_name?: string;
  pipeline_state?: Record<string, unknown>;
  entities?: EntityAssociation[];
  primaryEntityId?: string | null;
  entities_updated_at?: number;
};

type ListProjectsResponse = {
  status: boolean;
  data?: ProjectApi[];
  error?: string;
};


export async function adminEditOrgMember(data: any): Promise<boolean | null> {
  //console.log('adminEditOrgMember data:', data);
  const restOp = post({
    apiName: 'ntab',
    path: '/organisation/members/edit',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body: data
    }
  });
  const response = await restOp.response;
  const body = await response.body.json() as GetGenericResponse;
  if (!body.status) throw new Error(body.error || 'Failed to update organisation member');
  return true;
}


export async function inviteUser(data: any): Promise<boolean | null> {
  const restOp = post({
    apiName: 'ntab',
    path: '/organisation/members/add',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body: data
    }
  });
  const response = await restOp.response;
  const body = await response.body.json() as GetGenericResponse;
  if (!body.status) throw new Error(body.error || 'Failed to invite user');
  return true;
}




/// REMOVE THIS MOCK DATA AFTER TESTING

// `Status` and its `statuses` array are gone. They existed only to pick a project status at random
// in `mapProject`; removed 2026-10-09 along with the one caller, so nothing can reach for them
// again. The real status is `pipelineStatus`, derived from the latest run's own state.

/// RETURN MOCK DATA FOR TESTING PURPOSES ONLY




///////////////////////////// ENTITIES ////////////////////
// docs/adr/0003-entities.md. These THROW with the server's message rather than returning null:
// entity validation errors ("the primary entity must be one of the associated entities") are
// exactly what the user needs to see, and a bare null would turn them into a silent failure.

type LegalEntityApi = {
  item_id: string;
  code?: string;
  name: string;
  entity_type?: string;
  legal_name?: string;
  description?: string;
  aliases?: string[];
  item_status?: number;
  updated_at?: number;
};

type LegalEntityResponse = { status: boolean; data?: LegalEntityApi; error?: string | string[] };
type LegalEntityListResponse = { status: boolean; data?: LegalEntityApi[]; error?: string | string[] };

function mapLegalEntity(e: LegalEntityApi): LegalEntity {
  return {
    id: e.item_id,
    code: e.code,
    name: e.name,
    entityType: e.entity_type ?? '',
    legalName: e.legal_name ?? '',
    description: e.description ?? '',
    aliases: e.aliases ?? [],
    itemStatus: e.item_status ?? 1,
    updatedAt: e.updated_at
  };
}

function apiError(error: string | string[] | undefined, fallback: string): Error {
  return new Error(Array.isArray(error) ? error.join('; ') : error || fallback);
}

export async function listLegalEntities(): Promise<LegalEntity[]> {
  const restOp = get({
    apiName: 'ntab',
    path: '/entities',
    options: { headers: { Authorization: await getToken(), 'Content-Type': 'application/json' } }
  });
  const body = (await (await restOp.response).body.json()) as LegalEntityListResponse;
  if (!body.status) throw apiError(body.error, 'Failed to load entities');
  return (body.data ?? []).map(mapLegalEntity);
}

export async function createLegalEntity(payload: LegalEntityPayload): Promise<LegalEntity> {
  const restOp = post({
    apiName: 'ntab',
    path: '/entities',
    options: {
      headers: { Authorization: await getToken(), 'Content-Type': 'application/json' },
      body: payload
    }
  });
  const body = (await (await restOp.response).body.json()) as LegalEntityResponse;
  if (!body.status || !body.data) throw apiError(body.error, 'Failed to create entity');
  return mapLegalEntity(body.data);
}

export async function updateLegalEntity(
  entityId: string,
  payload: Partial<LegalEntityPayload> & { item_status?: number }
): Promise<LegalEntity> {
  const restOp = put({
    apiName: 'ntab',
    path: '/entities',
    options: {
      headers: { Authorization: await getToken(), 'Content-Type': 'application/json' },
      body: { entityId, ...payload }
    }
  });
  const body = (await (await restOp.response).body.json()) as LegalEntityResponse;
  if (!body.status || !body.data) throw apiError(body.error, 'Failed to update entity');
  return mapLegalEntity(body.data);
}

/** Archives — entities are never deleted, because documents, projects and run snapshots refer to them. */
export async function archiveLegalEntity(entityId: string): Promise<LegalEntity> {
  const restOp = del({
    apiName: 'ntab',
    path: `/entities/${encodeURIComponent(entityId)}`,
    options: { headers: { Authorization: await getToken() } }
  });
  const body = (await (await restOp.response).body.json()) as LegalEntityResponse;
  if (!body.status || !body.data) throw apiError(body.error, 'Failed to archive entity');
  return mapLegalEntity(body.data);
}


/**
 * Correct one document run's entity links (ADR 0003 decision 9). Throws with the server's message:
 * these are validation errors a user needs to read.
 */
export async function updateRunEntityLinks(
  documentId: string,
  runId: string,
  entities: { entityId: string; role: string | null }[],
  primaryEntityId: string | null
): Promise<RunEntityLinks> {
  const restOp = put({
    apiName: 'ntab',
    path: '/documents/runs/entities',
    options: {
      headers: { Authorization: await getToken(), 'Content-Type': 'application/json' },
      body: { documentId, runId, entities, primaryEntityId }
    }
  });
  const body = (await (await restOp.response).body.json()) as {
    status: boolean;
    data?: { links: RunEntityLinks; repointedArtefacts: number };
    error?: string | string[];
  };
  if (!body.status || !body.data) throw apiError(body.error, 'Failed to save entity links');
  return body.data.links;
}


///////////////////////////// PROJECTS ////////////////////


function mapProject(p: ProjectApi): Project {
  //console.log(p);
  const { status, versionCount, latestSuccessfulTimestamp } = getPipelineStatus(p);
  return {
    id: p.item_id,
    name: p.name,
    updatedAt: p.updated_at,
    description: p.description,
    itemStatus: p.item_status,
    type: p.type,
    ownerName: p.created_by_name,
    documents: p.documents,
    // Explicit mapper: every field the UI needs must be named here or it is silently dropped
    // (ADVICE.md — the third place in this codebase that pattern has bitten).
    entities: p.entities ?? [],
    primaryEntityId: p.primaryEntityId ?? null,
    entitiesUpdatedAt: p.entities_updated_at,
    // `projectStatus` used to be chosen at RANDOM here, from ["In Progress", "Active",
    // "Completed"], with a note to remove it once real status existed. It reached one live
    // consumer — the project chooser in the top bar — which therefore showed every project a
    // status that meant nothing and changed on reload. `pipelineStatus` below is the real one,
    // derived from the latest run's own state, and is what the chooser now reads.
    sector: 'Technology', // Remove this line after incorporating real data
    pipelineState: p.pipeline_state,
    pipelineStatus: status,
    versionCount: versionCount,
    latestSuccessfulTimestamp
  };
}

export async function listProjects(): Promise<Project[]> {
  const restOp = get({
    apiName: 'ntab',
    path: '/projects',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      }
    }
  });
  const response = await restOp.response;
  const body = await response.body.json() as ListProjectsResponse;
  //console.log('[SANITY] listProjects response body:', body);
  if (!body.status) throw new Error(body.error || 'Failed to list projects');
  const data = (body.data ?? []).map(mapProject);
  return data;
}

type ProjectTypeApi = {
  content?: {
    rows?: unknown[][];
    columns?: string[];
  };
};

type GetProjectTypesResponse = {
  status: boolean;
  data?: ProjectTypeApi;
};



type ProjectResponse = {
  status: boolean;
  data?: ProjectApi;
  error?: string | string[];
};


export async function addProject(obj: ProjectPayload): Promise<Project | null> {
  //console.log(obj);
  //return null;
  const restOp = post({
    apiName: 'ntab',
    path: '/projects', //path: '/projects/create',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body: obj
    }
  });
  const response = await restOp.response;
  const body = await response.body.json() as ProjectResponse;
  //console.log('[SANITY] listProjects response body:', body);
  if (!body.status || !body.data) return null;
  return mapProject(body.data);
}


export async function updateProject(identity: any, payload: ProjectPayload): Promise<Project | null> {
  //console.log(obj);
  //return null;
  const params = {
    ...identity,
    ...payload
    //projectId: id
  };

//console.log(params);
//return null;

  const restOp = put({
    apiName: 'ntab',
    path: `/projects`,
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body: params
    }
  });
  const response = await restOp.response;
  const body = await response.body.json() as ProjectResponse;
  // The server's refusals are the useful ones — "entity X is archived and cannot be associated"
  // names both the problem and the fix. Returning null instead discarded them, so a rejected save
  // closed the dialog and changed nothing, which is indistinguishable from a broken button.
  if (!body.status) throw apiError(body.error, 'Failed to update the project');
  if (!body.data) return null;
  return mapProject(body.data);
}

export async function deleteProject(id: string): Promise<boolean> {
  const restOp = del({
    apiName: 'ntab',
    path: `/projects/delete?projectId=${encodeURIComponent(id)}`,
    options: {
      headers: {
        Authorization: await getToken()
      }
    }
  });

  const response = await restOp.response;
  const body = await response.body.json() as { status: boolean };

  return Boolean(body.status);
}


///////////////////////////// LOOKUPS ////////////////////

type LookupContentApi = {
  rows?: unknown[][];
  columns?: string[];
};

type LookupRecordApi = {
  item_id?: string;
  id?: string;
  pk?: string;
  sk?: string;
  lookup_name?: string;
  lookupName?: string;
  description?: string;
  restricted?: number;
  content?: LookupContentApi;
  data?: LookupContentApi;
  created_at?: number;
  updated_at?: number;
  item_status?: number;
};

type LookupResponse = {
  status: boolean;
  data?: LookupRecordApi;
  error?: string;
  message?: string;
};

type LookupListResponse = {
  status: boolean;
  data?: LookupRecordApi[];
  error?: string;
  message?: string;
};

type LookupStatusResponse = {
  status: boolean;
  error?: string;
  message?: string;
};

export type LookupGrid = {
  columns: string[];
  description?: string;
  isRestricted?: boolean;
  rows: string[][];
  lookupName: string;
};

export type LookupItem = {
  lookupName: LookupName | string;
  description?: string;
  isRestricted?: boolean;
  content: {
    columns: string[];
    rows: string[][];
  };
  createdAt?: number;
  updatedAt?: number;
  itemStatus?: number;
};
export type LookupName =
  | 'ARTS'
  | 'CATS'
  | 'DOMN'
  | 'ECON'
  | 'GOAL'
  | 'MODL'
  | 'NTAB'
  | 'NTAB-META'
  | 'PTYPE'
  | 'SDOS'
  | 'SIGN';

//type LookupMode = 'list' | 'get' | 'put' | 'delete';
type LookupMode = 'query' | 'get' | 'delete' | 'delete-all' | 'batch' | 'put' | 'put-batch';
/*type LookupRequestBody = {
  pk: 'LOOKUP';
  mode: LookupMode;
  sk?: string;
  keys?: Array<{ pk: string; sk: string }>;
};*/
type LookupRequestBody = {
  pk: 'LOOKUP';
  mode: LookupMode;
  sk?: string;
  keys?: Array<{ pk: string; sk: string }>;
  description?: string;
  restricted?: number;
  content?: {
    columns: string[];
    rows: string[][];
  };
};
/*type LookupRequestBody = {
  pk: 'LOOKUP';
  mode: LookupMode;
  item_id?: string;
  sk?: string;
  lookupName?: string;
  content?: {
    columns: string[];
    rows: string[][];
  };
};*/



function normaliseLookupContent(content?: LookupContentApi | null): {
  columns: string[];
  rows: string[][];
} {
  const rawColumns = Array.isArray(content?.columns) ? content.columns : [];
  const rawRows = Array.isArray(content?.rows) ? content.rows : [];

  let columns = rawColumns.map((c, i) =>
    c == null || String(c).trim() === '' ? `Col ${i + 1}` : String(c)
  );

  let rows = rawRows.map((row) =>
    Array.isArray(row) ? row.map((cell) => (cell == null ? '' : String(cell))) : []
  );

  const maxCols = Math.max(
    columns.length,
    rows.reduce((m, r) => Math.max(m, r.length), 0)
  );

  if (maxCols === 0) {
    return {
      columns: ['Col 1'],
      rows: []
    };
  }

  if (columns.length < maxCols) {
    columns = [
      ...columns,
      ...Array.from(
        { length: maxCols - columns.length },
        (_, i) => `Col ${columns.length + i + 1}`
      )
    ];
  } else if (columns.length > maxCols) {
    columns = columns.slice(0, maxCols);
  }

  rows = rows.map((row) => {
    if (row.length < maxCols) {
      return [...row, ...Array(maxCols - row.length).fill('')];
    }
    if (row.length > maxCols) {
      return row.slice(0, maxCols);
    }
    return row;
  });

  return { columns, rows };
}

/*function mapLookup(record: LookupRecordApi): LookupItem {
  const content = normaliseLookupContent(record.content ?? record.data);

  return {
    id:
      String(
        record.item_id ??
        record.id ??
        record.sk ??
        record.lookup_name ??
        record.lookupName ??
        ''
      ) || '',
    lookupName: String(record.lookup_name ?? record.lookupName ?? record.sk ?? ''),
    content,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
    itemStatus: record.item_status
  };
}*/
function mapLookup(record: LookupRecordApi): LookupItem {
  const content = normaliseLookupContent(record.content ?? record.data);

  return {
    lookupName: String(record.sk ?? record.lookup_name ?? record.lookupName ?? ''),
    description: String(record.description ?? ''),
    isRestricted: record.restricted === 1,
    content,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
    itemStatus: record.item_status
  };
}

/*async function postLookupMode<T>(body: LookupRequestBody): Promise<T> {
  const restOp = post({
    apiName: 'ntab',
    path: '/results/mode',
    options: { body }
  });

  const response = await restOp.response;
  return (await response.body.json()) as T;
}*/
async function postLookupMode<T>(body: LookupRequestBody): Promise<T> {
  //console.log('postLookupMode - request body:', body);
  const restOp = post({
    apiName: 'ntab',
    path: '/results/mode',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body
    }
  });

  const response = await restOp.response;
  //console.log(response);
  const text = await response.body.text();
  //console.log(text);
  if (!text || !text.trim()) {
    throw new Error(
      `Empty response body from lookup API (${response}).`
    );
  }

  try {
    return JSON.parse(text) as T;
  } catch (err) {
    console.error('Lookup API returned non-JSON text:', text);
    throw new Error(
      `Lookup API returned invalid JSON (${response}).`
    );
  }
}

export function lookupRowsToObjects(
  lookup: Pick<LookupItem, 'content'> | LookupContentApi | null | undefined
): Record<string, unknown>[] {
  const content =
    lookup && 'content' in lookup
      ? lookup.content
      : lookup ?? { columns: [], rows: [] };

  const columns = Array.isArray(content?.columns) ? content.columns : [];
  const rows = Array.isArray(content?.rows) ? content.rows : [];

  return rows.map((row) =>
    Object.fromEntries(columns.map((header, i) => [header, row?.[i]]))
  );
}

export async function getLookupItem(
  lookupName: LookupName | string
): Promise<LookupItem | null> {
  const body = await postLookupMode<LookupResponse>({
    pk: 'LOOKUP',
    sk: lookupName,
    mode: 'get'
  });

  return body.status && body.data ? mapLookup(body.data) : null;
}

export async function getLookup<
  T extends Record<string, unknown> = Record<string, unknown>
>(lookupName: LookupName | string): Promise<T[]> {
  const item = await getLookupItem(lookupName);
  return item ? (lookupRowsToObjects(item) as T[]) : [];
}


/*export function lookupRowsToObjects(
  lookup: Pick<LookupItem, 'content'> | LookupContentApi | null | undefined
): Record<string, unknown>[] {
  const content =
    lookup && 'content' in lookup
      ? lookup.content
      : lookup ?? { columns: [], rows: [] };

  const columns = Array.isArray(content?.columns) ? content.columns : [];
  const rows = Array.isArray(content?.rows) ? content.rows : [];

  return rows.map((row) =>
    Object.fromEntries(columns.map((header, i) => [header, row?.[i]]))
  );
}*/

/*export async function getLookups(): Promise<LookupItem[]> {
  // Assumption: your Lambda supports mode: 'list' for pk LOOKUP.
  // If not, change this to whatever your backend expects.
  const body = await postLookupMode<LookupListResponse>({
    pk: 'LOOKUP',
    mode: 'list'
  });

  const items =
    body.status && Array.isArray(body.data) ? body.data.map(mapLookup) : [];

  return items.sort((a, b) =>
    a.lookupName.localeCompare(b.lookupName, undefined, {
      sensitivity: 'base'
    })
  );
}*/

export async function getLookups(): Promise<LookupItem[]> {
  const body = await postLookupMode<LookupListResponse>({
    pk: 'LOOKUP',
    mode: 'query'
  });

  if (!body.status) throw new Error(body.error || body.message || 'Failed to load lookups');

  const items = Array.isArray(body.data) ? body.data.map(mapLookup) : [];

  return items.sort((a, b) =>
    a.lookupName.localeCompare(b.lookupName, undefined, {
      sensitivity: 'base'
    })
  );
}


/*export async function getLookupItem(id: string): Promise<LookupItem | null> {
  const body = await postLookupMode<LookupResponse>({
    pk: 'LOOKUP',
    item_id: id,
    mode: 'get'
  });

  return body.status && body.data ? mapLookup(body.data) : null;
}*/
/*export async function getLookupItem(lookupName: string): Promise<LookupItem | null> {
  const body = await postLookupMode<LookupResponse>({
    pk: 'LOOKUP',
    sk: lookupName,
    mode: 'get'
  });

  return body.status && body.data ? mapLookup(body.data) : null;
}*/

/*export async function getLookup<
  T extends Record<string, unknown> = Record<string, unknown>
>(id: string): Promise<T[]> {
  const item = await getLookupItem(id);
  return item ? (lookupRowsToObjects(item) as T[]) : [];
}*/
/*export async function getLookup<T extends Record<string, unknown> = Record<string, unknown>>(
  lookupName: string
): Promise<T[]> {
  const item = await getLookupItem(lookupName);
  return item ? (lookupRowsToObjects(item) as T[]) : [];
}*/


export async function getLookupItemByName(lookupName: string): Promise<LookupItem | null> {
  const body = await postLookupMode<LookupResponse>({
    pk: 'LOOKUP',
    sk: lookupName,
    mode: 'get'
  });

  return body.status && body.data ? mapLookup(body.data) : null;
}

export async function getLookupByName<
  T extends Record<string, unknown> = Record<string, unknown>
>(lookupName: string): Promise<T[]> {
  const item = await getLookupItemByName(lookupName);
  return item ? (lookupRowsToObjects(item) as T[]) : [];
}

export async function getLookupOptions(
  lookupName: string
): Promise<Array<{ value: string; label: string }>> {
  return getLookupByName<{ value: string; label: string }>(lookupName);
}

export async function saveLookup(
  lookup: LookupGrid
): Promise<LookupItem | null> {
  const body = await postLookupMode<LookupResponse>({
    pk: 'LOOKUP',
    sk: lookup.lookupName.trim(),
    mode: 'put',
    description: lookup.description?.trim() || '',
    restricted: lookup.isRestricted ? 1 : 0,
    content: {
      columns: lookup.columns,
      rows: lookup.rows
    }
  });

  if (!body.status) throw new Error(body.error || body.message || 'Failed to save lookup');
  return body.data ? mapLookup(body.data) : null;
}
/*
export async function saveLookup(
  id: string | null,
  lookup: LookupGrid
): Promise<LookupItem | null> {
  const payload = {
    pk: 'LOOKUP',
    item_id: id ?? undefined,
    sk: lookup.lookupName.trim(),
    lookupName: lookup.lookupName.trim(),
    content: {
      columns: lookup.columns,
      rows: lookup.rows
    },

    // Assumption: your Lambda supports mode: 'put' for create/update.
    // Change to 'create'/'update'/'upsert' if needed.
    mode: 'put'
  };



  const body = await postLookupMode<LookupResponse>(payload);

  if (body.status && body.data) {
    return mapLookup(body.data);
  }

  if (body.status) {
    return {
      id: id ?? lookup.lookupName.trim(),
      lookupName: lookup.lookupName.trim(),
      content: {
        columns: lookup.columns,
        rows: lookup.rows
      }
    };
  }

  return null;
}
*/
/*export async function deleteLookup(
  lookup: Pick<LookupItem, 'id' | 'lookupName'> | string
): Promise<boolean> {
  const item =
    typeof lookup === 'string'
      ? { id: lookup, lookupName: '' }
      : lookup;

  // Assumption: your Lambda supports mode: 'delete'.
  const body = await postLookupMode<LookupStatusResponse>({
    pk: 'LOOKUP',
    item_id: item.id,
    sk: item.lookupName || item.id,
    mode: 'delete'
  });

  return Boolean(body.status);
}*/

export async function deleteLookup(
  lookup: Pick<LookupItem, 'lookupName'> | string
): Promise<boolean> {
  const lookupName = typeof lookup === 'string' ? lookup : lookup.lookupName;

  const body = await postLookupMode<LookupStatusResponse>({
    pk: 'LOOKUP',
    sk: lookupName,
    mode: 'delete'
  });

  if (!body.status) throw new Error(body.error || body.message || 'Failed to delete lookup');
  return true;
}

/*
type LookupTypeApi = {
  content?: {
    rows?: unknown[][];
    columns?: string[];
  };
};

type GetLookupTypesResponse = {
  status: boolean;
  data?: LookupTypeApi;
};

type Lookup = 'models' | 'project-types';

function rowsToObjects(pt: LookupTypeApi): Record<string, unknown>[] {
  const rows = pt.content?.rows ?? [];
  const columns = pt.content?.columns ?? [];

  return rows.map((row: unknown[]) =>
    Object.fromEntries(columns.map((h, i) => [h, row[i]]))
  );
}

function mapProjectTypes(pt: LookupTypeApi): ProjectType[] {
  const pTypes = rowsToObjects(pt);

  return pTypes.map((p, i) => ({
    code: String(p.code ?? p.Code ?? ''),
    title: String(p.title ?? p.Title ?? ''),
    show:
      String(p.show ?? '').trim() === '1' ||
      String(p.show ?? '').toLowerCase() === 'true',
    colour: String(p.colour ?? p.Colour ?? Object.values(berryPastels)[i])
  }));
}

function mapModelTypes(pt: LookupTypeApi): ModelType[] {
  const modelRows = rowsToObjects(pt);

  return modelRows.map((m) => ({
    value: String(m.value ?? ''),
    label: String(m.label ?? ''),
    isDefault:
      String(m.default ?? '').trim() === '1' ||
      String(m.default ?? '').toLowerCase() === 'true'
  }));
}

export function getLookup(type: 'models'): Promise<ModelType[]>;
export function getLookup(type: 'project-types'): Promise<ProjectType[]>;
export async function getLookup(
  type: Lookup
): Promise<ModelType[] | ProjectType[]> {
  let sk: 'MODL' | 'PTYPE';

  switch (type) {
    case 'models':
      sk = 'MODL';
      break;
    case 'project-types':
      sk = 'PTYPE';
      break;
  }

  const options = { pk: 'LOOKUP', sk, mode: 'get' };

  const restOp = post({
    apiName: 'ntab',
    path: '/results/mode',
    options: { body: options }
  });

  const response = await restOp.response;
  const body = (await response.body.json()) as GetLookupTypesResponse;

  switch (type) {
    case 'models':
      return body.status && body.data ? mapModelTypes(body.data) : [];
    case 'project-types':
      return body.status && body.data ? mapProjectTypes(body.data) : [];
  }
}
*/
/////////////////////////// CONFIG ////////////////

function formatApiError(error: string | string[] | undefined, fallback: string): string {
  if (Array.isArray(error)) return error.join('; ') || fallback;
  return error || fallback;
}

export async function getServiceStatus(): Promise<ServiceStatus | null> {
  const restOp = get({
    apiName: 'ntab',
    path: '/service-status',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      }
    }
  });

  const response = await restOp.response;
  const body = await response.body.json() as { status: boolean; data?: ServiceStatus; error?: string };

  if (!body.status) throw new Error(formatApiError(body.error, 'Failed to load service status'));
  return body.data ?? null;
}

export async function getConfigs(): Promise<ConfigByType | null> {
  const restOp = get({
    apiName: 'ntab',
    path: '/config',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      }
    }
  });

  const response = await restOp.response;
  //const body2 = await response.body.json();
  //console.log("Config response:", body2);
  const body = await response.body.json() as GetConfigsResponse;

  if (!body.status) throw new Error(formatApiError(body.error, 'Failed to load configs'));
  return body.data ?? null;
}

export async function updateConfig<T extends ConfigType>(
  type: T,
  config: ConfigByType[T]
): Promise<ConfigByType[T] | null> {
  //console.log(config);
  const restOp = put({
    apiName: 'ntab',
    path: `/config/${type}`,
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body: { config } as any
    }
  });

  const response = await restOp.response;
  const body = await response.body.json() as UpdateConfigResponse<T>;

  if (!body.status) throw new Error(formatApiError(body.error, 'Failed to update config'));
  return body.data?.config ?? null;
}

//////////////// DOCUMENTS /////////////////////
type DocumentApi = {
  item_id: string;
  name: string;
  updated_at?: number;
  updated_by_id?: string;
  updated_by_name?: string;
  description?: string;
  item_status?: number;
  created_by_id?: string;
  created_at?: number;
  type: string;
  created_by_name?: string;
  s3_key: string;
  file_type?: string;
  bucket: string;
  uploadUrl: string;
  expiresIn: number;
  pipeline_state?: Record<string, unknown>;
  entities?: EntityAssociation[];
  primaryEntityId?: string | null;
  entities_updated_at?: number;
};

type ListDocumentsResponse = {
  status: boolean;
  data?: DocumentApi[];
  error?: string;
};


export type VersionPromptsApi = {
  sk: string;
  updated_at?: number;
  updated_by_id?: string;
  updated_by_name?: string;
  item_status?: number;
  created_by_id?: string;
  created_at?: number;
  created_by_name?: string;
  content: string;
};


type ListVersionPromptsResponse = {
  status: boolean;
  data?: PromptResponseItem[];
  error?: string;
};

type ListVersionPromptResultsResponse = {
  status: boolean;
  data?: PromptResultResponseItem[];
  data2?: PromptResultResponseItem[];
  data3?: string;
  error?: string;
  usage?: RunPhaseUsage | null;
  runContext?: Record<string, unknown> | null;
  documentMetadata?: Record<string, unknown> | null;
};



/*function getPipelineStatus(
  doc?: DocumentApi
): { status: DocumentPipelineStatus; versionCount: number } {
  const pipelineState = doc?.pipeline_state;
  const itemStatus = doc?.item_status;
  const latestTimestamp = pipelineState?.latest_timestamp;
  //console.log("MMMMMM:", latestTimestamp);

  if (!pipelineState || latestTimestamp === undefined || latestTimestamp === null) {
    return { status: 'unknown', versionCount: 0 };
  }
  //console.log("QQQQQQQ:", latestTimestamp);
  //const latestRun = pipelineState?.runs?.[String(latestTimestamp)];
  //console.log("MMMMMM:", latestRun);
  const runs = (pipelineState.runs as Record<string, unknown>);
  const versionCount = Object.keys(runs).length;
  const pRun = runs[String(latestTimestamp)] as any;
  //const latestRun2 = pipelineState?.runs?[String(latestTimestamp)];
  //console.log("MMMMMM:", pRun);
  let status = (pRun?.state ?? 'unknown') as DocumentPipelineStatus;
  if (itemStatus! >= 9) {
    status = "deleted";
  }
  //console.log("------------------------------>>:", pRun.state);
  return { status, versionCount };
}*/

function getPipelineStatus(
  item?: DocumentApi | ProjectApi
): { status: DocumentPipelineStatus; versionCount: number, latestSuccessfulTimestamp: number } {
  const pipelineState = item?.pipeline_state;
  const itemStatus = item?.item_status;
  const latestTimestamp = pipelineState?.latest_timestamp;
 

  if (!pipelineState || latestTimestamp === undefined || latestTimestamp === null || latestTimestamp === 0) {
    return { status: 'unknown', versionCount: 0, latestSuccessfulTimestamp: 0 };
  }
  //console.log("QQQQQQQ:", latestTimestamp);
  //const latestRun = pipelineState?.runs?.[String(latestTimestamp)];
  //console.log("MMMMMM:", latestRun);
  const runs = (pipelineState.runs as Record<string, unknown>) ?? {};
  const versionCount = Object.keys(runs).length;
  const pRun = runs[String(latestTimestamp)] as any;
//const rawRuns = pipelineState?.runs ?? {};

const completedTimestamps = Object.entries(runs as Record<string, { state?: string }>)
  .filter(([, run]) => run.state === 'complete')
  .map(([timestamp]) => Number(timestamp))
  .filter(Number.isFinite);

const latestSuccessfulTimestamp =
  completedTimestamps.length > 0
    ? Math.max(...completedTimestamps)
    : 0;

  let status = (pRun?.state ?? 'unknown') as DocumentPipelineStatus;
  if (itemStatus! >= 9) {
    status = "deleted";
  }
  //console.log("------------------------------>>:", pRun.state);
  return { status, versionCount, latestSuccessfulTimestamp };
}

function mapDocument(p: DocumentApi): HubDocument {
  //console.log(p);
  //console.log(p.name);
  const { status, versionCount, latestSuccessfulTimestamp } = getPipelineStatus(p);
  //console.log(status, versionCount);
  return {
    id: p.item_id,
    name: p.name,
    updatedAt: p.updated_at,
    description: p.description,
    itemStatus: p.item_status,
    type: p.type,
    ownerName: p.created_by_name,
    pipelineState: p.pipeline_state,
    pipelineStatus: status,
    versionCount: versionCount,
    latestSuccessfulTimestamp,
    // A document's entities are its latest COMPLETE run's links (ADR 0003 decisions 6, 8) — derived
    // here, not stored on the document, so they cannot drift from the run that made them.
    ...(() => {
      const latest = latestCompleteRunLinks(p.pipeline_state);
      return {
        entities: latest?.links.entities.map(({ entityId }) => ({ entityId })) ?? [],
        primaryEntityId: latest?.links.primaryEntityId ?? null
      };
    })(),
    sector: 'Technology' // Remove this line after incorporating real data
  };
}

export async function listDocuments(): Promise<HubDocument[]> {
  const restOp = get({
    apiName: 'ntab',
    path: '/documents',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      }
    }
  });

  const response = await restOp.response;
  const body = await response.body.json() as ListDocumentsResponse;
  //console.log('[SANITY] listDocs response body:', body);

  if (!body.status) throw new Error(body.error || 'Failed to list documents');

  const data = (body.data ?? []).map(mapDocument);
  return data;
}




type DocumentResponse = {
  status: boolean;
  data?: DocumentApi;
};



export async function addDocument(payload: any): Promise<HubDocument | null> {
  //export async function addDocument(payload: any, file: File): Promise<boolean | null> {
  const { params, file } = payload;
  const restOp = post({
    apiName: 'ntab',
    path: '/documents/create',
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body: params
    }
  });

  const response = await restOp.response;
  const body = await response.body.json() as DocumentResponse;
  console.log('[SANITY] addDocument response body:', body);

  if (!body.status || !body.data) return null;

  //console.log('[SANITY] addDocument response body data:', body.data);

  const s3Response = await fetch(body.data.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream'
    },
    body: file
  });
  if (!s3Response.ok) {
    const errorText = await s3Response.text();
    console.log(errorText);
    throw errorText;
  }
  return mapDocument(body.data);
  //return true;//mapDocument(body.data);
}

export async function editDocument(payload: any): Promise<boolean | null> {
  //console.log(payload);
  const { documentId, name, type, description, entities, primaryEntityId } = payload;
  const params = {
    documentId,
    name,
    type,
    description,
    // Entities travel together with their primary; omitted entirely when the form did not supply
    // them, so an edit that does not touch entities cannot clear them.
    ...(entities ? { entities, primaryEntityId } : {})
  };
  const result = await updateDocument(params);
  return result;
}

export async function deleteDocument(payload: any): Promise<boolean | null> {
  //console.log(payload);
  const { documentId, runId, unarchive } = payload;
  const params = {
    documentId,
    item_status: unarchive ? 4 : 9,
    /*pipelinePatches: [
      {
        runId,
        path: ["state"],
        value: "deleted"
      },
    ]*/
  };
  const result = await updateDocument(params);
  return result;
}
/**
 * The Parsing phase's configured default for "re-use the existing parse".
 *
 * Read so the per-run switch can show the value that will actually be used, rather than defaulting
 * to off and contradicting the configuration. Returns null if the config cannot be read — the
 * caller then sends no preference and the server applies the default itself, so a failed read
 * degrades to the previous behaviour rather than silently forcing a value.
 */
export async function getDefaultReuseParse(): Promise<boolean | null> {
  try {
    const configs = await getConfigs();
    return configs?.parse?.parseOptions?.reusePreviousParse ?? false;
  } catch {
    return null;
  }
}

export async function rerunDocument(payload: any): Promise<boolean | null> {
  //console.log(payload);
  const { documentId, reuseParse = null } = payload;
  const params = {
    documentId,
    cancel: false,
    // Per-run options ride inside pipelineRunInit, which the API already accepts as an object.
    // null = no preference; the server falls back to the Parsing phase's configured default.
    pipelineRunInit: reuseParse === null ? {} : { reuseParse }
  }
  const result = await updateDocument(params);
  return result;
}
export async function cancelDocument(payload: any): Promise<boolean | null> {
  //console.log(payload);
  const { documentId, runId } = payload;
  const params = {
    documentId,
    cancel: true,
    pipelinePatches: [
      {
        runId,
        path: ["state"],
        value: "cancelling"
      },
    ]
  };
  const result = await updateDocument(params);
  return result;
}

async function updateDocument(params: any): Promise<boolean | null> {
  const restOp = put({
    apiName: 'ntab',
    path: `/documents`,
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      },
      body: params
    }
  });

  const response = await restOp.response;
  const body = await response.body.json() as DocumentResponse;

  if (!body.status || !body.data) return false;
  return true; //body.data;

}
/////////////////////////// VIEWPOINT ////////////////

/**
 * A ViewPoint is one run of one project — the analysis as at a point in time.
 *
 * Both ids are required and neither defaults to "latest". Every figure on the page belongs to a
 * particular run, and row ids are minted by that run's merge (ADR 0007), so a ViewPoint without its
 * run id is a set of numbers with no referent.
 */

/** One synthesis item's P1 and P2 output, keyed by SYNT code. */
export type ViewpointSynthesis = Record<string, { P1?: Record<string, any>; P2?: Record<string, any> }>;

export type ViewpointPhase = {
  state?: string | null;
  total: number;
  complete: number;
  failed: number;
  /** Contextualisation only: finsets no finding cited. Distinct from items that failed. */
  skipped?: string[];
  startTimestamp?: number | null;
  endTimestamp?: number | null;
};

/** One entry in the ViewPoint selector — index matches the run chips on the projects list. */
export type ViewpointSummary = {
  runId: string;
  index: number;
  state: string | null;
  endTimestamp: number | null;
};

export type Viewpoint = {
  project: {
    projectId: string;
    name: string | null;
    description: string | null;
    type: string | null;
    entities: unknown[];
    primaryEntityId: string | null;
    documents: unknown[];
  };
  viewpoint: {
    runId: string;
    state: string | null;
    phase: string | null;
    endTimestamp: number | null;
    phases: Record<string, ViewpointPhase>;
  };
  /** Every ViewPoint of this project, oldest first. */
  viewpoints: ViewpointSummary[];
  synthesis: ViewpointSynthesis;
};

/** Omit `runId` for the most recent ViewPoint; the response names the run it resolved to. */
export async function getViewpoint(projectId: string, runId?: string): Promise<Viewpoint> {
  const path = runId
    ? `/viewpoint/${encodeURIComponent(projectId)}/${encodeURIComponent(runId)}`
    : `/viewpoint/${encodeURIComponent(projectId)}`;
  const restOp = get({
    apiName: 'ntab',
    path,
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      }
    }
  });

  const response = await restOp.response;
  const body = (await response.body.json()) as unknown as { status: boolean; data?: Viewpoint; error?: string };

  if (!body.status || !body.data) throw new Error(formatApiError(body.error, 'Failed to load ViewPoint'));
  return body.data;
}

/**
 * The Cognitives pages for one ViewPoint (`GET /cognitives/{projectId}/{runId}`).
 *
 * Field names follow the COG P1/P2 schemas as stored, so a page reads `P1.mental_model.assessment`
 * exactly as the spec names it. `lineage` holds only what the characteristics cite; for runs made
 * before the prompt asked for citations it is empty and `recorded` is false.
 */
export type CognitiveCharacteristic = {
  characteristic_code?: string;
  title?: string;
  assessment?: string;
  understanding?: string;
  implications?: string;
  drivers?: string[];
  dependencies?: string[];
  tensions?: string[];
  uncertainties?: string[];
  supporting_mechanism_ids?: string[];
};

export type Cognitive = {
  code: string;
  name: string;
  P1: {
    cognitive_characteristics?: CognitiveCharacteristic[];
    mental_model?: { assessment?: string; assessment_confidence?: string; interpretation?: string };
  } | null;
  P2: {
    title?: string;
    executive?: { narrative?: string };
    standard?: { narrative?: string };
    detailed?: { narrative?: string };
  } | null;
};

export type CognitiveMechanism = {
  mechanism_id: string;
  goal: string;
  goal_name: string | null;
  title: string;
  mechanism: string;
  drivers: string[];
  relationships: string[];
  uncertainties: string[];
  implications: string;
  supporting_finding_ids: string[];
};

export type CognitiveFinding = {
  finding_id: string;
  domain: string;
  finding_text: string;
  implication: string;
  finset_references: { finset_code: string; row_ids: string[] }[];
  artefact_codes: string[];
};

/** The `mechanism → finding → finset rows` walk, resolved server-side; shared by Cognitives and Goals. */
export type Lineage = {
  recorded: boolean;
  mechanisms: Record<string, CognitiveMechanism>;
  findings: Record<string, CognitiveFinding>;
  missingMechanisms: string[];
  missingFindings: string[];
};

export type Cognitives = Pick<Viewpoint, 'project' | 'viewpoint' | 'viewpoints'> & {
  cognitives: Cognitive[];
  lineage: Lineage;
};

/** Omit `runId` for the most recent ViewPoint; the response names the run it resolved to. */
export async function getCognitives(projectId: string, runId?: string | null): Promise<Cognitives> {
  const path = runId
    ? `/cognitives/${encodeURIComponent(projectId)}/${encodeURIComponent(runId)}`
    : `/cognitives/${encodeURIComponent(projectId)}`;
  const restOp = get({
    apiName: 'ntab',
    path,
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      }
    }
  });

  const response = await restOp.response;
  const body = (await response.body.json()) as unknown as { status: boolean; data?: Cognitives; error?: string };

  if (!body.status || !body.data) throw new Error(formatApiError(body.error, 'Failed to load Cognitives'));
  return body.data;
}

/**
 * The Goals pages for one ViewPoint (`GET /goal-analysis/{projectId}/{runId}`).
 *
 * Field names follow the GOL P1/P2 schemas as stored, except that P1's `business_mechanisms` arrive
 * as `mechanism_ids` (ordered, qualified: `PBEQ-PFCFS.MECH-01`) pointing into `lineage.mechanisms`,
 * which holds every mechanism in the run. `presentation` is the amended P2 (Goals UI V4); runs made
 * before it have none.
 */
export type GoalSignal = { signal_code?: string; status?: string; value?: string | null; rationale?: string };

export type Goal = {
  code: string;
  name: string;
  P1: {
    mechanism_ids: string[];
    interpretation?: { assessment?: string; assessment_confidence?: string; narrative?: string };
    signals?: GoalSignal[];
  } | null;
  P2: {
    title?: string;
    executive?: { narrative?: string };
    standard?: { narrative?: string };
    detailed?: { narrative?: string };
    presentation?: {
      overview_headline?: string;
      overview_commentary?: string;
      key_consideration?: string;
      interpretation_summary?: string;
      mechanism_cards?: { mechanism_code?: string; summary?: string }[];
      watchpoints?: ({ text?: string } | string)[];
    };
  } | null;
};

export type GoalAnalysis = Pick<Viewpoint, 'project' | 'viewpoint' | 'viewpoints'> & {
  goals: Goal[];
  signals: Record<string, { name: string | null; group: string | null }>;
  lineage: Lineage;
  issues: { goal: string; kind: string; phase?: string; mechanism_code?: string | null }[];
};

/** Omit `runId` for the most recent ViewPoint; the response names the run it resolved to. */
export async function getGoalAnalysis(projectId: string, runId?: string | null): Promise<GoalAnalysis> {
  const path = runId
    ? `/goal-analysis/${encodeURIComponent(projectId)}/${encodeURIComponent(runId)}`
    : `/goal-analysis/${encodeURIComponent(projectId)}`;
  const restOp = get({
    apiName: 'ntab',
    path,
    options: {
      headers: {
        Authorization: await getToken(),
        'Content-Type': 'application/json'
      }
    }
  });

  const response = await restOp.response;
  const body = (await response.body.json()) as unknown as { status: boolean; data?: GoalAnalysis; error?: string };

  if (!body.status || !body.data) throw new Error(formatApiError(body.error, 'Failed to load Goals'));
  return body.data;
}
